import React, { useState, useRef } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Stepper, Step, StepLabel,
  Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, Snackbar, Divider, IconButton, Tooltip,
  InputAdornment
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import html2canvas from 'html2canvas';
const STEPS = ['Seleccionar Vallas', 'Fijar Precios', 'Datos del Cliente', 'Generar PDF'];

export default function ProposalBuilder({ espacios, mapContainerRef, proposalCart, setProposalCart, filters }) {
  const [activeStep, setActiveStep] = useState(0);
  const selectedIds = proposalCart || new Set();
  const setSelectedIds = setProposalCart || (() => {});
  const [prices, setPrices] = useState({}); // { Codigo: { precio_mensual: '', meses: '' } }
  const [clientName, setClientName] = useState('');
  const [clientLogo, setClientLogo] = useState(null); // base64
  const [clientLogoPreview, setClientLogoPreview] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null); // base64
  const [companyLogoPreview, setCompanyLogoPreview] = useState(null);
  const [proposalTitle, setProposalTitle] = useState('Propuesta Publicitaria');
  const [proposalDate, setProposalDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [footerText, setFooterText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const logoInputRef = useRef();
  const companyLogoInputRef = useRef();

  // Usamos los espacios filtrados que vienen por props (App.js ya los filtra por el sidebar)
  const filteredEspacios = espacios;

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const toggleSelect = (codigo) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(codigo)) next.delete(codigo);
      else next.add(codigo);
      return next;
    });
  };

  const toggleAll = () => {
    // Only toggle the visibly filtered spaces
    const filteredIds = filteredEspacios.map(e => e.Codigo);
    const allFilteredSelected = filteredIds.every(id => selectedIds.has(id));
    
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredIds.forEach(id => next.delete(id));
      } else {
        filteredIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setClientLogo(ev.target.result);
      setClientLogoPreview(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleCompanyLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setCompanyLogo(ev.target.result);
      setCompanyLogoPreview(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleGenerate = async () => {
    if (!clientName.trim()) {
      notify('Por favor ingresa el nombre del cliente', 'warning');
      return;
    }
    setGenerating(true);
    try {
      // Capture map screenshot
      let mapImage = null;
      if (mapContainerRef?.current) {
        try {
          const canvas = await html2canvas(mapContainerRef.current, { useCORS: true, backgroundColor: '#fff' });
          mapImage = canvas.toDataURL('image/png');
        } catch (e) {
          console.warn('Map capture failed:', e);
        }
      }

      const selectedSpaces = espacios
        .filter(e => selectedIds.has(e.Codigo))
        .map(e => ({
          ...e,
          precio_mensual: parseFloat(prices[e.Codigo]?.precio_mensual || 0),
          meses: parseInt(prices[e.Codigo]?.meses || 1),
        }));

      const _apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/espacios';
      const _apiRoot = _apiBase.replace(/\/api\/espacios.*$/, '');
      const response = await axios.post(`${_apiRoot}/api/generate-proposal`, {
        client_name: clientName,
        client_logo: clientLogo,
        company_logo: companyLogo,
        footer_text: footerText,
        proposal_title: proposalTitle,
        proposal_date: proposalDate,
        spaces: selectedSpaces,
        mapImage,
        notes,
      }, { responseType: 'blob' });

      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `propuesta-${clientName}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      notify('¡Propuesta generada exitosamente!');
      setActiveStep(0);
    } catch (err) {
      console.error(err);
      notify('Error al generar la propuesta. Intenta de nuevo.', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const selectedSpaces = espacios.filter(e => selectedIds.has(e.Codigo));
  const grandTotal = selectedSpaces.reduce((acc, e) => {
    const p = parseFloat(prices[e.Codigo]?.precio_mensual || 0);
    const m = parseInt(prices[e.Codigo]?.meses || 1);
    return acc + p * m;
  }, 0);

  const canNext = () => {
    if (activeStep === 0) return selectedIds.size > 0;
    if (activeStep === 2) return clientName.trim().length > 0;
    return true;
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 4, overflowY: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <DescriptionIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Generador de Propuestas</Typography>
          <Typography variant="body2" color="text.secondary">
            Crea propuestas PDF profesionales para tus clientes con precios y mapa.
          </Typography>
        </Box>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {STEPS.map(label => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

          {/* Right: count + table */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {filteredEspacios.length} vallas disponibles · <b>{selectedIds.size} seleccionadas</b>
              </Typography>
              <Button size="small" onClick={toggleAll}>
                {filteredEspacios.length > 0 && filteredEspacios.every(e => selectedIds.has(e.Codigo)) ? 'Deseleccionar listado' : 'Seleccionar listado'}
              </Button>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, overflow: 'auto', borderRadius: 2 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#0057B7', color: 'white', fontWeight: 700 } }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={filteredEspacios.length > 0 && filteredEspacios.every(e => selectedIds.has(e.Codigo))}
                        indeterminate={filteredEspacios.some(e => selectedIds.has(e.Codigo)) && !filteredEspacios.every(e => selectedIds.has(e.Codigo))}
                        onChange={toggleAll}
                        sx={{ color: 'white' }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>Código</TableCell>
                    <TableCell sx={{ color: 'white' }}>Tipo</TableCell>
                    <TableCell sx={{ color: 'white' }}>Dirección</TableCell>
                    <TableCell sx={{ color: 'white' }}>Zona</TableCell>
                    <TableCell sx={{ color: 'white' }}>Provincia</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEspacios.map(e => (
                    <TableRow
                      key={e.Codigo}
                      hover
                      selected={selectedIds.has(e.Codigo)}
                      onClick={() => toggleSelect(e.Codigo)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={selectedIds.has(e.Codigo)} />
                      </TableCell>
                      <TableCell><b>{e.Codigo}</b></TableCell>
                      <TableCell>{e.Tipo_Espacio}</TableCell>
                      <TableCell>{e.Direccion}</TableCell>
                      <TableCell>{e.Zona}</TableCell>
                      <TableCell>{e.Provincia}</TableCell>
                    </TableRow>
                  ))}
                  {filteredEspacios.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No se encontraron vallas con estos filtros.
                    </TableCell>
                  </TableRow>
                )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      )}

      {/* Step 1: Set prices */}
      {activeStep === 1 && (
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            Ingresa el precio mensual y la duración para cada valla seleccionada.
          </Alert>
          {selectedSpaces.map(e => (
            <Paper key={e.Codigo} variant="outlined" sx={{ p: 2, mb: 1.5, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 160 }}>
                  <Typography fontWeight={700} variant="body2">{e.Codigo}</Typography>
                  <Typography variant="caption" color="text.secondary">{e.Tipo_Espacio} · {e.Direccion}</Typography>
                </Box>
                <TextField
                  label="Precio/Mes (RD$)"
                  size="small"
                  type="number"
                  sx={{ width: 150 }}
                  value={prices[e.Codigo]?.precio_mensual || ''}
                  onChange={ev => setPrices(p => ({ ...p, [e.Codigo]: { ...p[e.Codigo], precio_mensual: ev.target.value } }))}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                />
                <TextField
                  label="Meses"
                  size="small"
                  type="number"
                  sx={{ width: 90 }}
                  value={prices[e.Codigo]?.meses || '1'}
                  onChange={ev => setPrices(p => ({ ...p, [e.Codigo]: { ...p[e.Codigo], meses: ev.target.value } }))}
                />
                <Box sx={{ minWidth: 100, textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">Subtotal</Typography>
                  <Typography fontWeight={700} color="primary.main">
                    RD$ {((parseFloat(prices[e.Codigo]?.precio_mensual || 0)) * parseInt(prices[e.Codigo]?.meses || 1)).toLocaleString()}
                  </Typography>
                </Box>
                <Tooltip title="Quitar valla">
                  <IconButton color="error" onClick={() => toggleSelect(e.Codigo)} sx={{ ml: 1 }}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>
          ))}
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Typography variant="h6" fontWeight={800} color="primary.dark">
              TOTAL: RD$ {grandTotal.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Step 2: Client info */}
      {activeStep === 2 && (
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {/* Logo upload */}
            {/* Logo uploads wrapper */}
            <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
              
              {/* Client Logo */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Paper
                  variant="outlined"
                  onClick={() => logoInputRef.current?.click()}
                  sx={{
                    width: 130, height: 130, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', borderRadius: 3, borderStyle: 'dashed', borderColor: 'primary.main',
                    overflow: 'hidden',
                    '&:hover': { bgcolor: 'rgba(0,87,183,0.03)' }
                  }}
                >
                  {clientLogoPreview
                    ? <img src={clientLogoPreview} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    : <Box sx={{ textAlign: 'center', color: 'primary.main' }}>
                        <AddPhotoAlternateIcon sx={{ fontSize: 36 }} />
                        <Typography variant="caption" sx={{ display: 'block' }}>Logo cliente</Typography>
                      </Box>
                  }
                </Paper>
                <input ref={logoInputRef} type="file" accept="image/*" hidden onChange={handleLogoUpload} />
                <Button size="small" onClick={() => logoInputRef.current?.click()}>
                  {clientLogoPreview ? 'Cambiar logo' : 'Subir logo cliente'}
                </Button>
              </Box>

              {/* Company Logo */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Paper
                  variant="outlined"
                  onClick={() => companyLogoInputRef.current?.click()}
                  sx={{
                    width: 130, height: 130, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', borderRadius: 3, borderStyle: 'dashed', borderColor: 'primary.main',
                    overflow: 'hidden',
                    '&:hover': { bgcolor: 'rgba(0,87,183,0.03)' }
                  }}
                >
                  {companyLogoPreview
                    ? <img src={companyLogoPreview} alt="logo empresa" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    : <Box sx={{ textAlign: 'center', color: 'primary.main' }}>
                        <AddPhotoAlternateIcon sx={{ fontSize: 36 }} />
                        <Typography variant="caption" sx={{ display: 'block' }}>Logo empresa</Typography>
                      </Box>
                  }
                </Paper>
                <input ref={companyLogoInputRef} type="file" accept="image/*" hidden onChange={handleCompanyLogoUpload} />
                <Button size="small" onClick={() => companyLogoInputRef.current?.click()}>
                  {companyLogoPreview ? 'Cambiar logo' : 'Subir logo empresa'}
                </Button>
              </Box>

            </Box>

            {/* Fields */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 260 }}>
              <TextField
                label="Nombre del cliente / empresa *"
                fullWidth
                value={clientName}
                onChange={e => setClientName(e.target.value)}
              />
              <TextField
                label="Título de la propuesta"
                fullWidth
                value={proposalTitle}
                onChange={e => setProposalTitle(e.target.value)}
                placeholder="Propuesta Publicitaria"
              />
              <TextField
                label="Fecha de la propuesta"
                type="date"
                fullWidth
                value={proposalDate}
                onChange={e => setProposalDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Notas adicionales (opcional)"
                fullWidth
                multiline
                rows={2}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Condiciones, vigencia, observaciones..."
              />
              <TextField
                label="Pie de página (opcional)"
                fullWidth
                multiline
                rows={2}
                value={footerText}
                onChange={e => setFooterText(e.target.value)}
                placeholder="Inmotion, S.A.S. | RNC ..."
              />
            </Box>
          </Box>

          {/* Summary */}
          <Paper variant="outlined" sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: '#f8f9fa' }}>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>Resumen de la propuesta</Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Cliente</Typography>
                <Typography fontWeight={600}>{clientName || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Vallas</Typography>
                <Typography fontWeight={600}>{selectedIds.size}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Total</Typography>
                <Typography fontWeight={700} color="primary.main">
                  RD$ {grandTotal.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Step 3: Generate */}
      {activeStep === 3 && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
          <DescriptionIcon sx={{ fontSize: 80, color: 'primary.main', opacity: 0.8 }} />
          <Typography variant="h6" fontWeight={700} textAlign="center">
            ¡Listo para generar la propuesta!
          </Typography>
          <Typography color="text.secondary" textAlign="center">
            Se generará un PDF profesional con el logo de <b>{clientName}</b>, las {selectedIds.size} vallas seleccionadas
            y el total de <b>RD$ {grandTotal.toLocaleString('es-DO', { minimumFractionDigits: 2 })}</b>.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <PictureAsPdfIcon />}
            onClick={handleGenerate}
            disabled={generating}
            sx={{ px: 5, py: 1.5, fontSize: '1rem' }}
          >
            {generating ? 'Generando PDF...' : 'Generar Propuesta PDF'}
          </Button>
        </Box>
      )}

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2, mt: 'auto' }}>
        <Button
          disabled={activeStep === 0}
          onClick={() => setActiveStep(s => s - 1)}
          variant="outlined"
        >
          Atrás
        </Button>
        {activeStep < STEPS.length - 1 && (
          <Button
            variant="contained"
            onClick={() => setActiveStep(s => s + 1)}
            disabled={!canNext()}
          >
            Siguiente
          </Button>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
